/**
 * Platform admin Settings tab: the section picker plus the active section's
 * cards.
 *
 * Adding an admin endpoint (CLAUDE.md §3.6)? Build a self-contained card in
 * components/admin/ (see OtpLockoutClearCard) and list it in the matching
 * section file under platform/settings/: Ai, Usage, Billing or Maintenance.
 * Sections return fragments so every card is a direct child of the gap-6
 * column below.
 */
import React from 'react';
import { View } from 'react-native';
import SettingsSectionTabs from '../settings/SettingsSectionTabs';
import AiSettingsSection from '../settings/AiSettingsSection';
import UsageSettingsSection from '../settings/UsageSettingsSection';
import BillingSettingsSection from '../settings/BillingSettingsSection';
import MaintenanceSettingsSection from '../settings/MaintenanceSettingsSection';
import type { School, SettingsSection } from '../platformAdminTypes';
import type { FeatureToggleState } from '../hooks/useFeatureToggle';
import type { SchoolActions } from '../hooks/useSchoolActions';

interface SettingsTabProps {
  settingsSection: SettingsSection;
  onSettingsSectionChange: (section: SettingsSection) => void;
  chatStreaming: FeatureToggleState;
  analysisStreaming: FeatureToggleState;
  agenticChat: FeatureToggleState;
  schools: School[];
  focusedSchoolId: number | null;
  schoolActions: SchoolActions;
  onCreateSchool: () => void;
  onPlatformDataChanged: () => void;
}

export default function SettingsTab({
  settingsSection,
  onSettingsSectionChange,
  chatStreaming,
  analysisStreaming,
  agenticChat,
  schools,
  focusedSchoolId,
  schoolActions,
  onCreateSchool,
  onPlatformDataChanged,
}: SettingsTabProps) {
  return (
    <View className="gap-6">
      <SettingsSectionTabs settingsSection={settingsSection} onChange={onSettingsSectionChange} />

      {settingsSection === 'ai' ? (
        <AiSettingsSection
          chatStreaming={chatStreaming}
          analysisStreaming={analysisStreaming}
          agenticChat={agenticChat}
        />
      ) : null}

      {settingsSection === 'usage' ? <UsageSettingsSection /> : null}

      {settingsSection === 'billing' ? (
        <BillingSettingsSection
          schools={schools}
          focusedSchoolId={focusedSchoolId}
          schoolActions={schoolActions}
          onCreateSchool={onCreateSchool}
          onPlatformDataChanged={onPlatformDataChanged}
        />
      ) : null}

      {settingsSection === 'maintenance' ? <MaintenanceSettingsSection /> : null}
    </View>
  );
}
